/**
 * Decides how the CRF Builder's Visits view presents the "Schedule of Events
 * Document" upload. The Schedule of Events is usually in the protocol, so the
 * upload prompt should appear only when the protocol is missing or did not
 * contain one (or a separate document has already been uploaded).
 *
 * Pure, no React or Firebase, so it is tested from functions' vitest web config.
 */

export type ScheduleUploadMode = 'loading' | 'fromProtocol' | 'full';

export interface ScheduleUploadInput {
  /** Both the protocol analysis and the study design have reported back. */
  loaded: boolean;
  /** protocolAnalysis/latest exists. */
  analysisExists: boolean;
  /** analysis.schedule.visits length. */
  protocolVisitCount: number;
  /** A separate Schedule of Events file is already uploaded (visitScheduleFile). */
  hasUploadedFile: boolean;
}

export function scheduleUploadMode(i: ScheduleUploadInput): ScheduleUploadMode {
  if (!i.loaded) return 'loading';
  if (i.hasUploadedFile) return 'full';
  if (!i.analysisExists) return 'full';
  if (i.protocolVisitCount <= 0) return 'full';
  return 'fromProtocol';
}

/**
 * Number of visits the protocol analysis found, read from the stored
 * protocolAnalysis/latest document at analysis.schedule.visits.
 */
export function protocolVisitCountOf(docData: unknown): number {
  const visits = (docData as Record<string, any> | null | undefined)?.analysis?.schedule?.visits;
  return Array.isArray(visits) ? visits.length : 0;
}

export function protocolLineText(count: number): string {
  return `Schedule of Events read from the protocol (${count} visit${count === 1 ? '' : 's'}).`;
}
